import React from "react";
import PropTypes from "prop-types";
import { Tooltip } from "react-tooltip";
import { useRandomId } from "../../hooks/useRandomId";
import "./percentChanged.scss";

const DEFAULT_POSITIVE = "#21e981";
const DEFAULT_NEGATIVE = "#c7343e";

function PercentChanged(props) {
  props = {
    toolTip: "",
    currencySymbol: "",
    fontSize: "16px",
    style: {},
    hideChange: false,
    hidePercentIfLessOne: false,
    positiveColor: DEFAULT_POSITIVE,
    negativeColor: DEFAULT_NEGATIVE,
    digits: 0,
    ...props
  };

  const id = useRandomId();

  const changeValuePercent = props.changeValue * 100;
  const percent = changeValuePercent.toFixed(props.digits);
  let percentValue = `${percent > 0 ? "+" : ""}${percent}%`;

  if (props.hidePercentIfLessOne) {
    if (Math.abs(changeValuePercent) < 1) {
      percentValue = "—";
    }
  }

  const isNeutral = !percentValue.includes("%");
  const isPositive = !isNeutral && props.changeValue > 0;
  const isDefaultColors =
    props.positiveColor === DEFAULT_POSITIVE && props.negativeColor === DEFAULT_NEGATIVE;

  // Swapped defaults pattern (e.g. attrition: more cancellations = bad even if value is positive).
  const isInvertedColors =
    props.positiveColor === DEFAULT_NEGATIVE && props.negativeColor === DEFAULT_POSITIVE;

  // For inverted colors, flip the visual class so light-theme bg/border matches text direction.
  const visuallyPositive = isInvertedColors ? !isPositive : isPositive;

  // Inline color only for truly custom (non-default, non-inverted) colors.
  // Default and inverted cases are fully handled by SCSS classes (text + bg + border).
  const colorStyle =
    !isDefaultColors && !isInvertedColors
      ? { color: isPositive ? props.positiveColor : props.negativeColor }
      : {};

  const toolTipId = "percent-change-tooltip" + id;
  return (
    <div
      className="percent-changed-container"
      style={{ fontSize: props.fontSize }}
      data-testid="percentChange"
    >
      {Number.isFinite(props.changeValue) && (
        <span
          data-tooltip-id={toolTipId}
          data-tooltip-content={props.toolTip}
          className={[
            "percent-changed",
            props.showBorder && "percent-changed--with-border",
            props.currencySymbol === "د.ك" && "percent-changed--float-left",
            isNeutral && "percent-changed--neutral",
            !isNeutral && visuallyPositive && "percent-changed--positive",
            !isNeutral && !visuallyPositive && "percent-changed--negative"
          ]
            .filter(Boolean)
            .join(" ")}
          style={colorStyle}
        >
          {percent >= -999 && percent <= 999 ? (
            <>
              {percentValue}
              <span className="percent-changed-note">{props.note}</span>
            </>
          ) : (
            <>{percent > 999 ? ">" : "<-"}999%</>
          )}
        </span>
      )}
      {props.toolTip ? <Tooltip id={toolTipId} float /> : ""}
    </div>
  );
}

PercentChanged.propTypes = {
  changeValue: PropTypes.any,
  showBorder: PropTypes.bool,
  note: PropTypes.string,
  toolTip: PropTypes.string,
  currencySymbol: PropTypes.string,
  fontSize: PropTypes.string,
  hideChange: PropTypes.bool,
  hidePercentIfLessOne: PropTypes.bool,
  digits: PropTypes.number,
  positiveColor: PropTypes.string,
  negativeColor: PropTypes.string
};

export default PercentChanged;
